import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import articles from "@/content/clanky.json";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";

const Block = ({ block }) => {
  if (block.t === "h") {
    return <h2 className="font-display text-2xl md:text-3xl text-[#252525] mt-12">{block.x}</h2>;
  }
  if (block.t === "ul") {
    return (
      <ul className="space-y-3">
        {block.items.map((item, i) => (
          <li key={i} className="flex gap-4">
            <span className="w-4 h-[1px] bg-[#B69A58] shrink-0 mt-3" aria-hidden="true" />
            <p className="text-base text-[#4A4A4A] leading-relaxed">{item}</p>
          </li>
        ))}
      </ul>
    );
  }
  return <p className="text-base md:text-lg text-[#4A4A4A] leading-relaxed">{block.x}</p>;
};

export default function ClankyDetail() {
  const { slug } = useParams();
  const article = articles.find((a) => a.slug === slug);

  useEffect(() => {
    if (article) {
      document.title = `${article.title} | Daniel Pekař`;
      const meta = document.querySelector('meta[name="description"]');
      if (meta) meta.setAttribute("content", article.excerpt);
    }
    return () => { document.title = "Daniel Pekař – finanční poradce Ústí nad Labem"; };
  }, [article]);

  return (
    <>
      <Navbar />
      <main className="pt-32 pb-24 md:pt-40 md:pb-32">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          {!article ? (
            <div data-testid="clanek-not-found">
              <h1 className="font-display text-4xl text-[#252525]">Tuto radu jsem nenašel</h1>
              <Link
                to="/rady"
                data-testid="clanek-back-overview"
                className="inline-flex items-center gap-2 mt-8 text-sm text-[#252525] border-b border-[#B69A58] pb-1 hover:text-[#B69A58] transition-colors duration-300"
              >
                <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
                Všechny rady pro klienty
              </Link>
            </div>
          ) : (
            <article data-testid="clanek-detail">
              <Link
                to="/rady"
                data-testid="clanek-back-link"
                className="inline-flex items-center gap-2 text-sm text-[#252525] border-b border-[#B69A58] pb-1 hover:text-[#B69A58] transition-colors duration-300"
              >
                <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
                Rady pro klienty
              </Link>

              <p className="text-xs uppercase tracking-widest text-[#B69A58] font-medium mt-10">
                {article.category}
                <span className="text-[#737373] font-normal">
                  {" · "}{new Date(article.date).toLocaleDateString("cs-CZ", { day: "numeric", month: "long", year: "numeric" })}
                </span>
              </p>
              <h1 className="font-display text-4xl md:text-5xl tracking-tight leading-[1.12] text-[#252525] mt-6">
                {article.title}
              </h1>

              <div className="mt-10 space-y-6">
                {article.body.map((block, i) => (
                  <Block key={i} block={block} />
                ))}
              </div>

              <div className="mt-14 border border-[#F0F0F0] p-8 md:p-10" data-testid="clanek-cta">
                <h2 className="font-display text-2xl text-[#252525]">Chcete to probrat osobně?</h2>
                <p className="text-base text-[#4A4A4A] leading-relaxed mt-3">
                  Ozvěte se mi — domluvíme nezávaznou konzultaci, osobně v Ústí nad Labem nebo online.
                </p>
                <Link
                  to="/#kontakt"
                  data-testid="clanek-consultation-link"
                  className="mt-8 inline-flex items-center justify-center border border-[#B69A58] bg-white text-[#252525] hover:bg-[#B69A58] hover:text-white transition-colors duration-300 px-8 py-4 text-xs uppercase tracking-widest font-medium"
                >
                  Domluvit konzultaci
                </Link>
              </div>
            </article>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}
